import { Box, Typography } from '@mui/material';
import type { MeasurementDto } from '@/features/harness/types';

/**
 * Recursively renders any JSON value as nested key/value rows. Deliberately generic (no
 * curated field list) — the profile is stored as raw JSONB precisely so nothing needs to be
 * hidden or filtered (AC4, FR-11); a curated panel would defeat that.
 */
function JsonValue({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return <span>—</span>;
  }
  if (Array.isArray(value)) {
    return (
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {value.map((item, i) => (
          <Box component="li" key={i}>
            <JsonValue value={item} />
          </Box>
        ))}
      </Box>
    );
  }
  if (typeof value === 'object') {
    return (
      <Box component="dl" sx={{ m: 0, pl: 2 }}>
        {Object.entries(value as Record<string, unknown>).map(([key, v]) => (
          <Box key={key} sx={{ display: 'flex', gap: 1 }}>
            <Box component="dt" sx={{ fontWeight: 500, minWidth: 160 }}>
              {key}
            </Box>
            <Box component="dd" sx={{ m: 0 }}>
              <JsonValue value={v} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }
  return <span>{String(value)}</span>;
}

export interface ExcitationProfileViewProps {
  /** All available Measurements (live store list, or a Batch's fetched Measurements). */
  measurements: MeasurementDto[];
  /** Selected Measurement ids in run order. */
  selectedIds: string[];
}

/**
 * Presentational core of the Excitation Profile panel (Story 1.4, AC4): renders the full,
 * unfiltered Excitation Profile for the single selected Measurement. Shown only when exactly one
 * Measurement is selected — the comparison view (N > 1) is about overlaid curves and side-by-side
 * parameters, not N full profiles. Reads nothing from the store, so the live harness view and the
 * connection-free review view (Story 3.1) both feed it their own measurements/selection.
 */
export function ExcitationProfileView({ measurements, selectedIds }: ExcitationProfileViewProps) {
  const selected =
    selectedIds.length === 1
      ? measurements.find((m) => m.id === selectedIds[0])
      : undefined;

  if (!selected) {
    return null;
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        Excitation Profile
      </Typography>
      {selected.excitationProfileJson == null ? (
        <Typography variant="body2" color="text.secondary">
          No excitation profile recorded for this Measurement.
        </Typography>
      ) : (
        <JsonValue value={selected.excitationProfileJson} />
      )}
    </Box>
  );
}
